/**
 * 🛰️ ParkingCabinet
 * 職責：停車場圖層、標籤（車種／計費小圖示）、分時圈高亮
 */

import { SovereignCabinet } from '../../core/cabinet-base.js';
import { SovereignEvents } from '../../core/cabinet-registry.js';
import {
    inferParkingTags,
    parkingBadgesHtml,
    parkingCapacityTableHtml,
    parkingFeeTableHtml,
    parkingSummaryText,
    parkingPassesViewFilter
} from '../../utils/parking-display-tags.js';
import {
    evStationSourceLabelZh,
    formatParkingProvenanceLines
} from '../../utils/parking-data-provenance.js';
import {
    evChargingRateTableHtml,
    parkingLinkedEvFeeHtml
} from '../../utils/ev-charging-rate-display.js';
import { filterOnSiteEvStations } from '../../utils/parking-ev-name-match.js';
import { appendMapClientQuery, isCompactMapClient } from '../../utils/map-client-profile.js';
import { googleMapsNavButtonHtml } from '../../utils/google-maps-nav.js';

const PARK_MIN_ZOOM_COMPACT = 12;

const PARK_LOCKED_POPUP_OPTS = {
    autoClose: false,
    closeOnClick: false,
    autoPan: false,
    keepInView: false,
    closeButton: true,
    closeOnEscapeKey: false
};

class ParkingCabinet extends SovereignCabinet {
    constructor() {
        super();
        this.parkingLayer = null;
        this.evStationLayer = null;
        this._parkingFc = null;
        this.lastIsoPolygon = null;
        this.showLabels = false;
        this.evOnly = false;
        this.vehicleFilters = new Set();
        this.feeFilters = new Set();
        this.searchQ = '';
        this.showEvStations = false;
        this._evStationsReady = true; // B：/api/live/ev/stations
        this._evStationsFc = null;
        this._highlightedUids = new Set();
        this._lockedMapPopup = null;
        this._clusterLibsLoaded = false;
        this._viewportTimer = null;
        this.loadAssets('/js/cabinets/parking');
    }

    onMount() {
        this.ensureGlobalStyles();
        const toggle = this.shadowRoot.getElementById('toggleParking');
        const toggleLabels = this.shadowRoot.getElementById('toggleParkingLabels');
        const toggleEvOnly = this.shadowRoot.getElementById('toggleParkingEvOnly');
        const toggleEvStations = this.shadowRoot.getElementById('toggleEvStations');
        const parkingSearch = this.shadowRoot.getElementById('parkingSearch');

        toggle?.addEventListener('change', (e) => {
            if (e.target.checked) {
                this.loadAndRender();
            } else {
                this.evOnly = false;
                this.searchQ = '';
                this.showLabels = false;
                this.showEvStations = false;
                if (toggleLabels) toggleLabels.checked = false;
                if (toggleEvOnly) toggleEvOnly.checked = false;
                if (toggleEvStations) toggleEvStations.checked = false;
                this.clearClassFilters();
                if (parkingSearch) parkingSearch.value = '';
                this.clearLayer();
                this.clearEvStationLayer();
            }
            this.syncSubToggleState();
        });

        toggleLabels?.addEventListener('change', (e) => {
            this.showLabels = !!e.target.checked;
            this.refreshMarkerIcons();
            this.syncLayerLabelFlags();
        });

        toggleEvOnly?.addEventListener('change', (e) => {
            this.evOnly = !!e.target.checked;
            this.renderParkingFromCache();
        });

        toggleEvStations?.addEventListener('change', (e) => {
            this.showEvStations = !!e.target.checked;
            if (this.showEvStations) this.loadEvStationsLayer();
            else this.clearEvStationLayer();
            this.updateStatusFromLayers();
        });

        this.shadowRoot.querySelectorAll('[data-park-vehicle],[data-park-fee]').forEach((el) => {
            el.addEventListener('change', () => {
                this.syncClassFilters();
                this.renderParkingFromCache();
            });
        });
        parkingSearch?.addEventListener('input', (e) => {
            this.searchQ = String(e.target.value || '');
            this.renderParkingFromCache();
        });

        this.busSub = this.bus.on(SovereignEvents.ISOC_RENDERED, (data) => {
            this.lastIsoPolygon = data.polygon;
            // 禁止自動勾選／載入停車場（分時圈不得連動開全圖層）
            if (this.parkingLayer) {
                this.updateSpatialHighlights(this.lastIsoPolygon, true);
            }
        });

        this.bus.on(SovereignEvents.SCREENSHOT_START, () => {
            if (this.lastIsoPolygon && this.parkingLayer) {
                this.updateSpatialHighlights(this.lastIsoPolygon, true);
            }
        });

        this.bus.on(SovereignEvents.ISOC_CLEARED, () => {
            this.lastIsoPolygon = null;
            if (this.parkingLayer) {
                this.updateSpatialHighlights(null, false);
            }
        });
    }

    afterAssetsMounted() {
        this.syncSubToggleState();
    }

    /** @deprecated 相容舊呼叫 */
    syncLabelToggleState() {
        this.syncSubToggleState();
    }

    syncSubToggleState() {
        const on = !!this.shadowRoot?.getElementById('toggleParking')?.checked;
        const toggleLabels = this.shadowRoot?.getElementById('toggleParkingLabels');
        const toggleEvOnly = this.shadowRoot?.getElementById('toggleParkingEvOnly');
        const toggleEvStations = this.shadowRoot?.getElementById('toggleEvStations');
        const parkingSearch = this.shadowRoot?.getElementById('parkingSearch');

        if (toggleLabels) {
            toggleLabels.disabled = !on;
            if (!on) {
                toggleLabels.checked = false;
                this.showLabels = false;
            }
        }
        if (toggleEvOnly) {
            toggleEvOnly.disabled = !on;
            if (!on) {
                toggleEvOnly.checked = false;
                this.evOnly = false;
            }
        }
        if (toggleEvStations) {
            // 充電站圖層：主開關開著即可操作；資料未接入時勾選只提示，不打 TDX
            toggleEvStations.disabled = !on;
            if (!on) {
                toggleEvStations.checked = false;
                this.showEvStations = false;
            }
        }
        this.shadowRoot?.querySelectorAll('[data-park-vehicle],[data-park-fee]').forEach((el) => {
            el.disabled = !on;
            if (!on) el.checked = false;
        });
        if (!on) {
            this.vehicleFilters.clear();
            this.feeFilters.clear();
        }
        if (parkingSearch) {
            parkingSearch.disabled = !on;
            if (!on) {
                parkingSearch.value = '';
                this.searchQ = '';
            }
        }
    }

    syncClassFilters() {
        this.vehicleFilters = new Set(
            [...(this.shadowRoot?.querySelectorAll('[data-park-vehicle]:checked') || [])]
                .map((el) => el.getAttribute('data-park-vehicle'))
                .filter(Boolean)
        );
        this.feeFilters = new Set(
            [...(this.shadowRoot?.querySelectorAll('[data-park-fee]:checked') || [])]
                .map((el) => el.getAttribute('data-park-fee'))
                .filter(Boolean)
        );
    }

    clearClassFilters() {
        this.shadowRoot?.querySelectorAll('[data-park-vehicle],[data-park-fee]').forEach((el) => {
            el.checked = false;
        });
        this.vehicleFilters.clear();
        this.feeFilters.clear();
    }

    featureHasEv(props = {}) {
        if (props.h_ev_recharging === true || Number(props.h_ev_recharging) === 1) return true;
        if (Number(props.h_capacity?.ev) > 0) return true;
        if (Array.isArray(props.h_ev_linked_stations) && props.h_ev_linked_stations.length > 0) return true;
        return !!this.tagsFor(props).vehicles?.ev;
    }

    filteredParkingFc() {
        const base = this._parkingFc;
        if (!base?.features) return { type: 'FeatureCollection', features: [] };
        return {
            ...base,
            features: base.features.filter((f) => {
                const p = f.properties || {};
                if (this.evOnly && !this.featureHasEv(p)) return false;
                return parkingPassesViewFilter(p, {
                    vehicles: [...this.vehicleFilters],
                    fees: [...this.feeFilters],
                    q: this.searchQ
                });
            })
        };
    }

    /** C：停車場圖層開啟時，場內關聯站不重複畫點 */
    filteredEvStationsFc() {
        const base = this._evStationsFc;
        if (!base?.features) return { type: 'FeatureCollection', features: [] };
        const parkingOn = !!this.shadowRoot?.getElementById('toggleParking')?.checked && !!this.parkingLayer;
        if (!parkingOn) return base;
        return {
            ...base,
            features: base.features.filter((f) => {
                const role = f.properties?.h_map_role;
                const linked = f.properties?.h_linked_parking_uid;
                if (role === 'at_parking' || linked) return false;
                return true;
            })
        };
    }

    ensureGlobalStyles() {
        if (document.querySelector('link[data-parking-map-css]')) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = '/css/parking-map-overlays.css?v=17.0.735';
        link.setAttribute('data-parking-map-css', '17.0.735');
        document.head.appendChild(link);
    }

    async updateSpatialHighlights(polygon, shouldHighlight) {
        if (!this.parkingLayer) return;
        this._highlightedUids.clear();

        this.parkingLayer.eachLayer(function walk(marker) {
            if (marker.eachLayer && !marker.getLatLng) {
                marker.eachLayer(walk);
                return;
            }
            if (!marker.getLatLng) return;
            const props = marker.feature?.properties || {};
            const uid = props.h_uid || props.h_name;
            let inside = false;
            if (shouldHighlight && polygon && window.turf) {
                const pt = turf.point([marker.getLatLng().lng, marker.getLatLng().lat]);
                inside = turf.booleanPointInPolygon(pt, polygon);
            }
            if (inside && uid) this._highlightedUids.add(String(uid));
            marker.setZIndexOffset?.(inside ? 1000 : 0);
            this.applyMarkerIcon(marker);
        }.bind(this));
        this.syncLayerLabelFlags();
    }

    onUnmount() {
        if (this.busSub) this.busSub();
        this.clearLayer();
    }

    onMapReady(map) {
        this.map = map;
        this.ensureGlobalStyles();
        const onViewport = () => {
            if (!isCompactMapClient()) return;
            const on = !!this.shadowRoot?.getElementById('toggleParking')?.checked;
            if (!on) return;
            clearTimeout(this._viewportTimer);
            this._viewportTimer = setTimeout(() => this.loadAndRender(), 280);
        };
        this.map.on('moveend', onViewport);
        this.map.on('zoomend', onViewport);
    }

    ensureMarkerClusterLib() {
        if (this._clusterLibsLoaded && typeof L?.markerClusterGroup === 'function') {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            if (!document.querySelector('link[data-park-mc-css="1"]')) {
                for (const href of [
                    '/lib/markercluster/MarkerCluster.css',
                    '/lib/markercluster/MarkerCluster.Default.css'
                ]) {
                    const link = document.createElement('link');
                    link.rel = 'stylesheet';
                    link.href = href;
                    link.setAttribute('data-park-mc-css', '1');
                    document.head.appendChild(link);
                }
            }
            if (typeof L?.markerClusterGroup === 'function') {
                this._clusterLibsLoaded = true;
                resolve();
                return;
            }
            const script = document.createElement('script');
            script.src = '/lib/markercluster/leaflet.markercluster.js';
            script.onload = () => {
                this._clusterLibsLoaded = true;
                resolve();
            };
            script.onerror = () => reject(new Error('markercluster load failed'));
            document.head.appendChild(script);
        });
    }

    bboxQuery() {
        const bounds = this.map.getBounds().pad(0.12);
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        return appendMapClientQuery(new URLSearchParams({
            xmin: String(sw.lng),
            ymin: String(sw.lat),
            xmax: String(ne.lng),
            ymax: String(ne.lat),
            zoom: String(this.map.getZoom())
        }));
    }

    escapeHtml(value) {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;');
    }

    tagsFor(props) {
        return inferParkingTags(props || {});
    }

    buildMarkerHtml(props, highlighted) {
        const tags = this.tagsFor(props);
        const badges = this.showLabels ? parkingBadgesHtml(tags) : '';
        const hl = highlighted ? ' is-hl' : '';
        return `<div class="park-mark${hl}"><span class="park-p">P</span>${badges}</div>`;
    }

    markerIcon(props, highlighted) {
        const labeled = this.showLabels;
        return L.divIcon({
            className: 'custom-parking-compact',
            html: this.buildMarkerHtml(props, highlighted),
            iconSize: labeled ? [110, 18] : [16, 16],
            iconAnchor: labeled ? [8, 9] : [8, 8]
        });
    }

    applyMarkerIcon(marker) {
        const props = marker.feature?.properties || {};
        const uid = String(props.h_uid || props.h_name || '');
        const highlighted = this._highlightedUids.has(uid);
        marker.setIcon(this.markerIcon(props, highlighted));
    }

    refreshMarkerIcons() {
        if (!this.parkingLayer) return;
        const walk = (marker) => {
            if (marker.eachLayer && !marker.getLatLng) {
                marker.eachLayer(walk);
                return;
            }
            if (marker.setIcon) this.applyMarkerIcon(marker);
        };
        this.parkingLayer.eachLayer(walk);
    }

    buildPopupHtml(props, latlng = null) {
        const name = this.escapeHtml(props.h_name || props.name || '未命名停車場');
        const address = this.escapeHtml(props.h_addr || props.address || '地址不詳');
        const phone = this.escapeHtml(props.h_tel || props.phone || '暫無電話');
        const badges = parkingBadgesHtml(this.tagsFor(props));
        const capTable = parkingCapacityTableHtml(props);
        const feeTable = parkingFeeTableHtml(props.h_fees || props.fee_info || '');
        const linked = filterOnSiteEvStations(
            Array.isArray(props.h_ev_linked_stations) ? props.h_ev_linked_stations : [],
            props.h_name || props.name || ''
        );
        const evFeeTable = parkingLinkedEvFeeHtml(linked, (uid) => this.lookupEvChargingRate(uid));
        const capBlock = capTable
            ? `<div class="park-fee-label">🅿️ 車位容量：</div>${capTable}`
            : '';
        const evBlock = linked.length
            ? `<div class="park-fee-label">⚡ 場內充電站（${linked.length}）：</div>
               <ul class="park-ev-list">${linked.map((s) => {
                   const sn = this.escapeHtml(s.name || s.station_id || '');
                   const pts = Number(s.charging_points) || Number(s.spaces) || 0;
                   const src = this.escapeHtml(this.linkedEvSourceLabel(s));
                   return `<li>${sn}${pts ? `（${pts}樁）` : ''}${src ? `<div class="park-ev-src">來源：${src}</div>` : ''}</li>`;
               }).join('')}</ul>`
            : (props.h_ev_recharging
                ? `<div class="park-meta">⚡ 具充電服務（描述／旗標）</div>`
                : '');
        const provLines = formatParkingProvenanceLines(props.h_data_provenance, {
            updatedAt: this._parkingFc?.updated_at
        }).map((line) => this.escapeHtml(line));
        const sourceBlock = provLines.length
            ? `<div class="park-popup-source">${provLines.join('<br>')}</div>`
            : '';
        const navBtn = googleMapsNavButtonHtml(latlng || props);
        return `
            <div class="park-popup-inner">
                <div class="park-popup-drag-handle" data-park-drag-handle title="按住拖曳移動彈窗">
                    <div class="park-popup-kicker">🅿️ 停車場 <span class="park-popup-drag-hint">⠿ 可拖曳 · 已鎖定 · 點 × 關閉</span></div>
                    <div class="park-title">
                        <span class="park-p">P</span>${badges}
                        <span class="park-title-text">${name}</span>
                    </div>
                </div>
                <div class="park-detail">
                    ${capBlock}
                    ${evBlock}
                    <div class="park-fee-label">💰 收費標準：</div>
                    ${feeTable}
                    ${evFeeTable ? `<div class="park-fee-label">⚡ 充電費：</div>${evFeeTable}` : ''}
                    <div class="park-meta">📍 <b>地址：</b>${address}</div>
                    ${navBtn}
                    <div class="park-meta">📞 <b>電話：</b>${phone}</div>
                    ${sourceBlock}
                </div>
            </div>
        `;
    }

    lookupEvChargingRate(uid) {
        const feat = (this._evStationsFc?.features || []).find((f) => f.properties?.h_uid === uid);
        return feat?.properties?.h_charging_rate || '';
    }

    needsEvRateHydration(props) {
        const linked = Array.isArray(props?.h_ev_linked_stations) ? props.h_ev_linked_stations : [];
        if (!linked.length) return false;
        return linked.every((s) => !String(s?.charging_rate || s?.h_charging_rate || '').trim());
    }

    async ensureEvStationsCache() {
        if (this._evStationsFc || !this._evStationsReady) return this._evStationsFc;
        try {
            const res = await fetch('/api/live/ev/stations', { credentials: 'include', cache: 'no-store' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            this._evStationsFc = await res.json();
        } catch {
            this._evStationsReady = false;
            this._evStationsFc = null;
        }
        return this._evStationsFc;
    }

    linkedEvSourceLabel(s) {
        if (s?.source_label || s?.h_source || s?.source) {
            return evStationSourceLabelZh(s);
        }
        const uid = s?.station_uid;
        const feat = uid
            ? (this._evStationsFc?.features || []).find((f) => f.properties?.h_uid === uid)
            : null;
        return evStationSourceLabelZh(feat?.properties || {});
    }

    buildEvStationPopupHtml(props, latlng = null) {
        const name = this.escapeHtml(props.h_name || '充電站');
        const addr = this.escapeHtml(props.h_addr || '');
        const op = this.escapeHtml(props.h_operator_name || '');
        const svc = this.escapeHtml(props.h_service_time || '');
        const parkFee = this.escapeHtml(String(props.h_parking_rate || '').trim());
        const pts = Number(props.h_charging_points) || Number(props.h_spaces) || 0;
        const rateTable = evChargingRateTableHtml(props.h_charging_rate || '');
        const srcLines = formatParkingProvenanceLines(props.h_data_provenance, {
            updatedAt: this._evStationsFc?.updated_at
        }).map((line) => this.escapeHtml(line));
        if (srcLines[0] === '資料來源：停車場開放資料') {
            const evSrc = evStationSourceLabelZh(props);
            srcLines[0] = `資料來源：${this.escapeHtml(evSrc || '充電站開放資料')}`;
        }
        const navBtn = googleMapsNavButtonHtml(latlng || props);
        return `
            <div class="park-popup-inner">
                <div class="park-title">
                    <span class="park-p park-p-ev">⚡</span>
                    <span class="park-title-text">${name}</span>
                </div>
                <div class="park-detail">
                    ${op ? `<div class="park-meta">業者：${op}</div>` : ''}
                    ${pts ? `<div class="park-meta">樁／位：${pts}</div>` : ''}
                    ${svc ? `<div class="park-meta">服務：${svc}</div>` : ''}
                    ${rateTable ? `<div class="park-fee-label">充電費</div>${rateTable}` : ''}
                    ${parkFee ? `<div class="park-meta">停車費：${parkFee}</div>` : ''}
                    ${addr ? `<div class="park-meta">📍 ${addr}</div>` : ''}
                    ${navBtn}
                    ${srcLines.length ? `<div class="park-popup-source">${srcLines.join('<br>')}</div>` : ''}
                </div>
            </div>
        `;
    }

    getParkingSummary(props) {
        return parkingSummaryText(this.tagsFor(props));
    }

    setStatus(text, { hint = false } = {}) {
        const el = this.shadowRoot?.getElementById('status-text');
        if (!el) return;
        el.innerText = text;
        el.classList.toggle('is-hint', !!hint);
    }

    updateStatusFromLayers() {
        const total = this._parkingFc?.features?.length || 0;
        const countLeafLayers = (layer) => {
            let n = 0;
            const walk = (child) => {
                if (child.eachLayer && !child.getLatLng) {
                    child.eachLayer(walk);
                    return;
                }
                n += 1;
            };
            layer.eachLayer(walk);
            return n;
        };
        const shown = this.parkingLayer ? countLeafLayers(this.parkingLayer) : 0;
        const evTotal = this._evStationsFc?.features?.length || 0;
        const evShown = this.evStationLayer
            ? (() => {
                let n = 0;
                this.evStationLayer.eachLayer(() => { n += 1; });
                return n;
            })()
            : 0;
        const bits = [];
        if (this.parkingLayer) {
            const tags = [];
            const vehZh = { car: '汽車', motorcycle: '機車', heavy: '重機', large: '大型', handicapped: '身障' };
            const feeZh = { hourly: '計時', perVisit: '計次', monthly: '月租' };
            for (const id of this.vehicleFilters) if (vehZh[id]) tags.push(vehZh[id]);
            for (const id of this.feeFilters) if (feeZh[id]) tags.push(feeZh[id]);
            if (this.evOnly) tags.push('充電');
            if (this.searchQ.trim()) tags.push('搜尋');
            bits.push(tags.length
                ? `停車場 ${shown}/${total}（${tags.join('+')}）`
                : `停車場 ${shown}`);
        }
        if (this.showEvStations) {
            if (!this._evStationsReady || !this._evStationsFc) {
                bits.push('充電站待接入');
            } else {
                bits.push(`充電站 ${evShown}${evTotal ? `/${evTotal}` : ''}`);
            }
        }
        if (!bits.length) {
            this.setStatus('已隱藏');
            return;
        }
        this.setStatus(`✅ ${bits.join(' · ')}`, {
            hint: this.showEvStations && (!this._evStationsReady || !this._evStationsFc)
        });
    }

    renderParkingFromCache() {
        if (!this.map || !this._parkingFc) return;
        this.mountParkingGeoJson(this.filteredParkingFc());
        if (this.showEvStations) this.mountEvStationsFromCache();
        this.syncSubToggleState();
        this.updateStatusFromLayers();
    }

    mountEvStationsFromCache() {
        if (!this.map || !this._evStationsFc) return;
        this.clearEvStationLayer();
        const fc = this.filteredEvStationsFc();
        this.evStationLayer = L.geoJSON(fc, {
            pointToLayer: (feature, latlng) => L.marker(latlng, {
                icon: L.divIcon({
                    className: 'custom-ev-station',
                    html: '<div class="ev-mark" title="充電站"><span class="ev-bolt">⚡</span></div>',
                    iconSize: [18, 18],
                    iconAnchor: [9, 9]
                })
            }),
            onEachFeature: (feature, layer) => {
                layer.bindPopup(this.buildEvStationPopupHtml(feature.properties || {}, feature), {
                    className: 'park-popup',
                    maxWidth: 340
                });
            }
        }).addTo(this.map);
        window.evStationLayer = this.evStationLayer;
    }

    /**
     * 充電站獨立圖層（柜內開關）。C：場內關聯站在 P 顯示 ⚡，此層略過。
     */
    async loadEvStationsLayer() {
        if (!this.map || !this.showEvStations) return;
        if (!this._evStationsReady) {
            this.updateStatusFromLayers();
            return;
        }
        try {
            await this.ensureEvStationsCache();
            if (!this._evStationsFc) throw new Error('EV_STATIONS_EMPTY');
            this.mountEvStationsFromCache();
        } catch (err) {
            this._evStationsReady = false;
            this._evStationsFc = null;
            this.setStatus('⚠️ 充電站圖層載入失敗（已略過）', { hint: true });
        }
        this.updateStatusFromLayers();
    }

    syncLayerLabelFlags() {
        if (!this.parkingLayer) return;
        this.parkingLayer.labelsVisible = this.showLabels;
        this.parkingLayer.labelGroup = this.parkingLayer;
    }

    mountParkingGeoJson(fc) {
        if (!this.map) return;
        if (this.parkingLayer) {
            this.map.removeLayer(this.parkingLayer);
            this.parkingLayer = null;
        }
        this.parkingLayer = L.geoJSON(fc, {
            pointToLayer: (feature, latlng) => L.marker(latlng, {
                icon: this.markerIcon(feature.properties || {}, false)
            }),
            onEachFeature: (feature, layer) => {
                layer.on('click', (e) => {
                    L.DomEvent.stopPropagation(e);
                    this.openLockedParkPopup(feature.properties || {}, e.latlng || layer.getLatLng());
                });
            }
        });
        if (isCompactMapClient() && typeof L.markerClusterGroup === 'function') {
            const cluster = L.markerClusterGroup({
                maxClusterRadius: 48,
                disableClusteringAtZoom: 16,
                spiderfyOnMaxZoom: true,
                showCoverageOnHover: false
            });
            cluster.addLayer(this.parkingLayer);
            cluster.addTo(this.map);
            this.parkingLayer = cluster;
        } else {
            this.parkingLayer.addTo(this.map);
        }
        window.parkingLayer = this.parkingLayer;
        this.syncLayerLabelFlags();
        if (this.lastIsoPolygon) {
            this.updateSpatialHighlights(this.lastIsoPolygon, true);
        }
    }

    async loadAndRender() {
        if (!this.map) return;
        this.setStatus('載入中...');
        try {
            if (isCompactMapClient()) {
                const zoom = this.map.getZoom();
                if (zoom < PARK_MIN_ZOOM_COMPACT) {
                    this.clearLayer();
                    this.setStatus(`請放大至 ${PARK_MIN_ZOOM_COMPACT} 級以上顯示停車場（現在 ${Math.round(zoom)}）`);
                    return;
                }
                await this.ensureMarkerClusterLib().catch((e) =>
                    console.warn('[ParkingCabinet] markercluster unavailable', e)
                );
                const res = await fetch(`/api/live/parking/off-street/bbox?${this.bboxQuery()}`, {
                    credentials: 'include'
                });
                if (!res.ok) throw new Error('無法載入停車數據');
                const bboxDoc = await res.json();
                bboxDoc._compact = true;
                this._parkingFc = bboxDoc;
            } else if (!this._parkingFc || this._parkingFc._compact) {
                const res = await fetch('/api/live/parking/off-street', { credentials: 'include', cache: 'no-store' });
                if (!res.ok) throw new Error('無法載入停車數據');
                this._parkingFc = await res.json();
            }
            this.mountParkingGeoJson(this.filteredParkingFc());
            this.syncSubToggleState();
            if (this.showEvStations) await this.loadEvStationsLayer();
            this.updateStatusFromLayers();
        } catch (err) {
            this.setStatus('❌ 載入失敗');
        }
    }

    clearEvStationLayer() {
        if (this.map && this.evStationLayer) {
            this.map.removeLayer(this.evStationLayer);
            this.evStationLayer = null;
            window.evStationLayer = null;
        }
    }

    closeLockedParkPopup() {
        if (!this._lockedMapPopup || !this.map) return;
        this.teardownDraggableParkPopup(this._lockedMapPopup);
        this.map.closePopup(this._lockedMapPopup);
        this._lockedMapPopup = null;
    }

    async openLockedParkPopup(props, latlng) {
        if (!this.map || !latlng) return;
        this.closeLockedParkPopup();
        const popup = L.popup({
            ...PARK_LOCKED_POPUP_OPTS,
            maxWidth: 380,
            className: 'park-popup'
        }).setLatLng(latlng).setContent(this.buildPopupHtml(props, latlng));

        popup.once('remove', () => {
            this.teardownDraggableParkPopup(popup);
            if (this._lockedMapPopup === popup) this._lockedMapPopup = null;
        });

        popup.openOn(this.map);
        this._lockedMapPopup = popup;
        const el = popup.getElement();
        if (el) {
            this.setupDraggableParkPopup(popup, el);
            L.DomEvent.disableClickPropagation(el);
            L.DomEvent.disableScrollPropagation(el);
        }
        if (this.needsEvRateHydration(props)) {
            await this.ensureEvStationsCache();
            if (this._lockedMapPopup === popup) {
                popup.setContent(this.buildPopupHtml(props, latlng));
            }
        }
    }

    patchPopupPositionForDrag(popup) {
        if (!popup || popup._parkPositionPatch) return;
        popup._parkPositionPatch = true;
        popup._parkOrigUpdatePosition = popup._updatePosition.bind(popup);
        popup._updatePosition = function parkPopupUpdatePosition() {
            if (this._parkDragged) return;
            return this._parkOrigUpdatePosition();
        };
    }

    readPopupTranslate(el) {
        const tr = window.getComputedStyle(el).transform;
        if (!tr || tr === 'none') return { x: 0, y: 0 };
        if (tr.startsWith('matrix3d(')) {
            const parts = tr.slice(9, -1).split(',').map((v) => parseFloat(v.trim()));
            return { x: parts[12] || 0, y: parts[13] || 0 };
        }
        if (tr.startsWith('matrix(')) {
            const parts = tr.slice(7, -1).split(',').map((v) => parseFloat(v.trim()));
            return { x: parts[4] || 0, y: parts[5] || 0 };
        }
        return { x: 0, y: 0 };
    }

    setupDraggableParkPopup(popup, rootEl) {
        if (!popup || !rootEl || !this.map) return;
        const popupPane = rootEl.classList?.contains('leaflet-popup')
            ? rootEl
            : rootEl.closest('.leaflet-popup');
        const handle = rootEl.querySelector('[data-park-drag-handle]');
        if (!popupPane || !handle || handle._parkDragSetup) return;

        this.patchPopupPositionForDrag(popup);
        handle._parkDragSetup = true;
        const map = this.map;

        const endDrag = () => {
            if (!popup._parkDragSession) return;
            const s = popup._parkDragSession;
            if (!s.moved) popup._parkDragged = false;
            map.dragging.enable();
            popup._parkDragSession = null;
            popupPane.classList.remove('park-popup-dragging');
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', onTouchEnd);
        };

        const applyDelta = (dx, dy) => {
            const s = popup._parkDragSession;
            if (!s) return;
            if (Math.abs(dx) > 2 || Math.abs(dy) > 2) s.moved = true;
            popup._parkDragged = true;
            popupPane.classList.add('is-dragged');
            const x = Math.round(s.originX + dx);
            const y = Math.round(s.originY + dy);
            popupPane.style.transform = `translate3d(${x}px, ${y}px, 0px)`;
        };

        const startDrag = (clientX, clientY) => {
            const origin = this.readPopupTranslate(popupPane);
            popup._parkDragSession = {
                startX: clientX,
                startY: clientY,
                originX: origin.x,
                originY: origin.y,
                moved: false
            };
            popup._parkDragged = true;
            map.dragging.disable();
            popupPane.classList.add('park-popup-dragging');
        };

        const onMove = (e) => {
            if (!popup._parkDragSession) return;
            L.DomEvent.preventDefault(e);
            applyDelta(e.clientX - popup._parkDragSession.startX, e.clientY - popup._parkDragSession.startY);
        };
        const onUp = () => endDrag();
        const onTouchMove = (e) => {
            const t = e.touches[0];
            if (!popup._parkDragSession || !t) return;
            L.DomEvent.preventDefault(e);
            applyDelta(t.clientX - popup._parkDragSession.startX, t.clientY - popup._parkDragSession.startY);
        };
        const onTouchEnd = () => endDrag();

        const onHandleDown = (e) => {
            if (e.type === 'mousedown' && e.button !== 0) return;
            L.DomEvent.stopPropagation(e);
            L.DomEvent.preventDefault(e);
            const pt = e.touches?.[0] || e;
            startDrag(pt.clientX, pt.clientY);
            if (e.type === 'mousedown') {
                document.addEventListener('mousemove', onMove);
                document.addEventListener('mouseup', onUp);
            } else {
                document.addEventListener('touchmove', onTouchMove, { passive: false });
                document.addEventListener('touchend', onTouchEnd);
            }
        };

        handle.addEventListener('mousedown', onHandleDown);
        handle.addEventListener('touchstart', onHandleDown, { passive: false });

        popup._parkDragTeardown = () => {
            endDrag();
            handle.removeEventListener('mousedown', onHandleDown);
            handle.removeEventListener('touchstart', onHandleDown);
            handle._parkDragSetup = false;
            popup._parkDragged = false;
            popupPane.classList.remove('is-dragged', 'park-popup-dragging');
            popupPane.style.transform = '';
            if (popup._parkOrigUpdatePosition) popup._parkOrigUpdatePosition();
        };
    }

    teardownDraggableParkPopup(popup) {
        popup?._parkDragTeardown?.();
        if (popup) popup._parkDragTeardown = null;
    }

    clearLayer() {
        this.closeLockedParkPopup();
        if (this.map && this.parkingLayer) {
            this.map.removeLayer(this.parkingLayer);
            this.parkingLayer = null;
            window.parkingLayer = null;
            this._highlightedUids.clear();
        }
        this.clearEvStationLayer();
        this.setStatus('已隱藏');
    }
}

customElements.define('parking-cabinet', ParkingCabinet);

function dock() {
    if (window.appCore) {
        const instance = document.createElement('parking-cabinet');
        const container = document.getElementById('socket-parking');
        if (container) {
            container.innerHTML = '';
            container.appendChild(instance);
            window.appCore.registerCabinet('parking', instance);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dock);
} else {
    dock();
}
